// Kho emoji cho ô soạn tin chat khách.
//
// Emoji ở đây là NỘI DUNG tin nhắn (ký tự Unicode nằm trong `body`), không
// phải icon của giao diện — icon của bảng chọn vẫn là icon vector (lucide).
// Cột `customer_chat_messages.body` là text và máy chủ chỉ trim + đếm rune
// (`validateMessageContent`), nên không cần đổi gì ở tầng máy chủ hay CSDL.
//
// Danh sách khai TAY chứ không nạp bộ emoji đầy đủ của Unicode: nhân viên
// chat với khách studio ảnh cần vài trăm cái đúng việc, còn bộ đầy đủ là
// 3800 ký tự phải tải về kèm bảng tra tên — đắt mà không ai cuộn tới.

/** Nhóm trong bảng chọn. `recent` không có danh sách cứng — đọc từ máy người dùng. */
export type EmojiGroupKey = "recent" | "faces" | "people" | "love" | "photo" | "things" | "symbols";

export type EmojiEntry = {
  /** Ký tự chèn vào tin nhắn. */
  readonly ch: string;
  /** Từ khoá tiếng Việt cho ô tìm kiếm và cho trình đọc màn hình. */
  readonly kw: string;
};

export type EmojiGroup = {
  readonly key: EmojiGroupKey;
  /** Nhãn tiếng Việt — dùng làm aria-label của thẻ nhóm. */
  readonly label: string;
  readonly items: readonly EmojiEntry[];
};

/** Gõ tay nhanh hơn viết object cho từng cái. */
const nhom = (pairs: readonly (readonly [string, string])[]): readonly EmojiEntry[] =>
  pairs.map(([ch, kw]) => ({ ch, kw }));

export const EMOJI_GROUPS: readonly EmojiGroup[] = [
  {
    key: "faces",
    label: "Cảm xúc",
    items: nhom([
      ["😀", "cười tươi"],
      ["😁", "cười rạng rỡ"],
      ["😂", "cười ra nước mắt"],
      ["🤣", "cười bò"],
      ["😊", "cười hiền"],
      ["🙂", "cười nhẹ"],
      ["😌", "nhẹ nhõm hài lòng"],
      ["😍", "yêu quá mắt tim"],
      ["🥰", "thương yêu"],
      ["😘", "gửi nụ hôn"],
      ["🤗", "ôm"],
      ["🤩", "phấn khích sao"],
      ["😎", "ngầu kính"],
      ["🥳", "ăn mừng tiệc"],
      ["😇", "thiên thần ngoan"],
      ["🤭", "che miệng cười"],
      ["😉", "nháy mắt"],
      ["😴", "ngủ buồn ngủ"],
      ["🥺", "năn nỉ xin"],
      ["😢", "khóc buồn"],
      ["😭", "khóc to"],
      ["😤", "tức giận xì khói"],
      ["😳", "bất ngờ đỏ mặt"],
      ["🤔", "suy nghĩ"],
      ["😅", "cười trừ mồ hôi"],
      ["😬", "nhăn mặt khó xử"],
      ["🫣", "che mắt ngại"],
      ["🤐", "im lặng khoá miệng"],
      ["😶", "không nói gì"],
      ["🥹", "cảm động rưng rưng"],
      ["🫠", "tan chảy mệt"],
      ["😮", "ồ ngạc nhiên"],
    ]),
  },
  {
    key: "people",
    label: "Cử chỉ",
    items: nhom([
      ["👍", "đồng ý tốt like"],
      ["👌", "ổn oke"],
      ["👏", "vỗ tay hoan hô"],
      ["🙏", "cảm ơn xin nhờ"],
      ["🤝", "bắt tay hợp tác"],
      ["✌️", "chữ v thắng"],
      ["🤞", "mong chúc may"],
      ["💪", "cố lên mạnh"],
      ["👋", "chào tạm biệt"],
      ["🫶", "trái tim bằng tay"],
      ["🙌", "reo mừng hai tay"],
      ["🤲", "xin mời hai tay"],
      ["✍️", "viết ghi"],
      ["🙋", "giơ tay hỏi"],
      ["💁", "hướng dẫn chỉ"],
      ["💃", "múa nhảy"],
      ["👰", "cô dâu cưới"],
      ["🤵", "chú rể"],
      ["🧑‍🎨", "thợ ảnh nghệ sĩ hậu kỳ"],
      ["💅", "làm nail móng"],
      ["🫰", "tim bằng ngón tay"],
      ["👀", "xem nhìn"],
      ["🤦", "bó tay thất vọng"],
      ["🤷", "không biết chịu"],
    ]),
  },
  {
    key: "love",
    label: "Tình cảm",
    items: nhom([
      ["❤️", "tim đỏ yêu"],
      ["🧡", "tim cam"],
      ["💛", "tim vàng"],
      ["💚", "tim xanh lá"],
      ["💙", "tim xanh dương"],
      ["💜", "tim tím"],
      ["🤍", "tim trắng"],
      ["💕", "hai tim"],
      ["💖", "tim lấp lánh"],
      ["💝", "tim quà tặng"],
      ["💐", "bó hoa tặng"],
      ["🌸", "hoa đào hoa anh đào"],
      ["🌹", "hoa hồng"],
      ["🌺", "hoa râm bụt"],
      ["🌻", "hoa hướng dương"],
      ["🍀", "cỏ may mắn"],
      ["🎀", "nơ ruy băng"],
      ["💍", "nhẫn cưới"],
      ["✨", "lấp lánh long lanh"],
      ["🥂", "nâng ly chúc mừng"],
    ]),
  },
  {
    key: "photo",
    label: "Chụp ảnh",
    items: nhom([
      ["📷", "máy ảnh chụp"],
      ["📸", "chụp đèn flash"],
      ["🎥", "quay phim máy quay"],
      ["🎬", "cắt cảnh quay"],
      ["🎞", "cuộn phim"],
      ["🖼", "khung ảnh tranh"],
      ["🪞", "gương"],
      ["💄", "son trang điểm"],
      ["👗", "váy trang phục"],
      ["👔", "áo sơ mi vest"],
      ["🕶", "kính mát"],
      ["💡", "đèn ý tưởng"],
      ["🎨", "màu vẽ concept"],
      ["🖌", "cọ vẽ"],
      ["🌟", "ngôi sao nổi bật"],
      ["⭐", "sao đánh giá"],
      ["🏆", "cúp giải thưởng"],
      ["🎉", "chúc mừng bùng nổ"],
      ["🎊", "hoa giấy chúc mừng"],
      ["🍰", "bánh sinh nhật"],
      ["🎂", "bánh kem tuổi"],
      ["🎁", "quà tặng"],
      ["🪄", "phép thuật hậu kỳ"],
      ["🖨", "in ảnh máy in"],
    ]),
  },
  {
    key: "things",
    label: "Công việc",
    items: nhom([
      ["📅", "lịch ngày hẹn"],
      ["⏰", "báo thức giờ"],
      ["⏳", "chờ hết giờ"],
      ["📍", "địa chỉ vị trí"],
      ["🏠", "nhà cơ sở"],
      ["🚗", "xe di chuyển"],
      ["📞", "điện thoại gọi"],
      ["📱", "điện thoại di động"],
      ["💬", "tin nhắn chat"],
      ["📝", "ghi chú viết"],
      ["📋", "danh sách kiểm"],
      ["📦", "kiện hàng giao"],
      ["💵", "tiền mặt"],
      ["💳", "thẻ thanh toán"],
      ["🧾", "hoá đơn phiếu"],
      ["🏦", "ngân hàng chuyển khoản"],
      ["🔗", "đường dẫn link"],
      ["📤", "gửi đi"],
      ["☂️", "mưa dù"],
      ["☀️", "nắng trời"],
    ]),
  },
  {
    key: "symbols",
    label: "Ký hiệu",
    items: nhom([
      ["✅", "xong đã duyệt"],
      ["☑️", "đã tick chọn"],
      ["❌", "sai huỷ"],
      ["⚠️", "cảnh báo lưu ý"],
      ["❗", "quan trọng"],
      ["❓", "câu hỏi"],
      ["💯", "một trăm điểm"],
      ["🔥", "hot cháy"],
      ["🆗", "ok được"],
      ["🔔", "thông báo nhắc"],
      ["🔒", "khoá riêng tư"],
      ["♻️", "làm lại chỉnh lại"],
      ["➕", "thêm"],
      ["➖", "bớt"],
      ["🔄", "đổi lại xoay"],
      ["🙇", "xin lỗi cúi đầu"],
    ]),
  },
];

/** Khoá localStorage giữ emoji hay dùng. Riêng từng máy, KHÔNG lên máy chủ. */
export const RECENT_EMOJI_KEY = "gnpf.chat.recentEmoji";

/** Số emoji "dùng gần đây" giữ lại — vừa đúng hai hàng của lưới 8 cột. */
export const MAX_RECENT = 16;

/** Bảng tra ký tự → từ khoá, để hàng "gần đây" cũng đọc được tên. */
const KEYWORD_BY_CHAR = new Map<string, string>(
  EMOJI_GROUPS.flatMap((group) => group.items.map((item) => [item.ch, item.kw] as const)),
);

/** Từ khoá của một emoji. Ký tự lạ (bộ cũ, người khác gõ) trả chuỗi rỗng. */
export function emojiKeyword(ch: string): string {
  return KEYWORD_BY_CHAR.get(ch) ?? "";
}

/**
 * Emoji hay dùng, mới nhất trước.
 *
 * Bọc try/catch và lọc lại theo bảng tra: cửa sổ ẩn danh thì đọc localStorage
 * ném lỗi, còn dữ liệu cũ có thể chứa emoji đã rút khỏi kho — để nguyên thì
 * bảng hiện ô trống không tên.
 */
export function recentEmojis(): readonly string[] {
  try {
    if (typeof localStorage === "undefined") return [];
    const raw = localStorage.getItem(RECENT_EMOJI_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((ch): ch is string => typeof ch === "string" && KEYWORD_BY_CHAR.has(ch))
      .slice(0, MAX_RECENT);
  } catch {
    return [];
  }
}

/** Ghi nhận vừa dùng một emoji; trả về danh sách mới để vẽ lại ngay. */
export function rememberEmoji(ch: string): readonly string[] {
  const next = [ch, ...recentEmojis().filter((old) => old !== ch)].slice(0, MAX_RECENT);
  try {
    if (typeof localStorage !== "undefined") {
      localStorage.setItem(RECENT_EMOJI_KEY, JSON.stringify(next));
    }
  } catch {
    // Máy chặn lưu trữ thì bảng vẫn dùng được, chỉ không nhớ qua lần sau.
  }
  return next;
}

/**
 * Tìm emoji theo từ khoá tiếng Việt.
 *
 * So khớp KHÔNG dấu để gõ "cam on" cũng ra 🙏 — nhân viên gõ nhanh trên
 * điện thoại thường bỏ dấu. Chuỗi rỗng trả về rỗng (nơi gọi hiện nhóm thường).
 */
export function searchEmoji(query: string): readonly EmojiEntry[] {
  const q = boDau(query.trim());
  if (!q) return [];
  const seen = new Set<string>();
  const hits: EmojiEntry[] = [];
  for (const group of EMOJI_GROUPS) {
    for (const item of group.items) {
      if (seen.has(item.ch)) continue;
      if (boDau(item.kw).includes(q)) {
        seen.add(item.ch);
        hits.push(item);
      }
    }
  }
  return hits;
}

/** Bỏ dấu tiếng Việt + hạ chữ thường. Dùng NFD nên "đ" phải thay tay. */
function boDau(text: string): string {
  return text
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/đ/gi, "d")
    .toLowerCase();
}
